// START IMPORTS
// Refresh lib imports
import {
  AppConfig,
  AppConstants,
  ReportContextProvider,
} from '@ltvco/refresh-lib/ctx';
import { Grid, Box, styled } from '@ltvco/refresh-lib/theme';
import { UnclaimedMoneyReportLoading } from '@ltvco/refresh-lib/unclaimedMoney';
import {
  ReportLoading,
  useSession,
  type SearchFormProps,
} from '@ltvco/refresh-lib/v1';
import {
  reportGetters,
  type SearchReportResponseData,
  SearchFooter,
} from '@ltvco/refresh-lib/v2';
import {
  useQueryClient,
  useMutation,
  useFeatureIsOn,
} from '@ltvco/refresh-lib/vendors';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

// Internal - components
import {
  EmailReport,
  PersonReport,
  PropertyReport,
  PhoneReport,
  SocialReport,
  EmailFraudReport,
  PhoneFraudReport,
  UnclaimedMoneyReport,
} from 'components/reports';
import { Sidenav } from 'components/sidenav/Sidenav';
// Internal - assets
import imgUnclaimedMoney from 'images/img_unclaimed_money.svg';
// END IMPORTS

// START HELPER_FUNCTIONS
function useQueryParams(params: string) {
  return useMemo(() => new URLSearchParams(params), [params]);
}
// END HELPER_FUNCTIONS

// START STYLED_COMPONENTS
const ReportBody = styled('div')(({ theme }) => ({
  width: '100%',
  margin: 'auto',

  [theme.breakpoints.up('md')]: {
    margin: 'auto',
  },

  [theme.breakpoints.up('lg')]: {
    margin: 'auto',
    maxWidth: '1100px',
  },
}));

const FooterSearchContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2.5),

  // Not only the ReportBody, but also the AppContent (App.tsx) are limited to 1600px max width
  // So with this, we make sure the footer search is always 100% of the screen width
  [theme.breakpoints.up(1600)]: {
    width: '100vw',
    // marginLeft: 'calc(-100vw / 2 + 1600px / 2)',
    // marginRight: 'calc(-100vw / 2 + 1600px / 2)',
  },
}));
// END STYLED_COMPONENTS

// START INTERFACES_AND_TYPES
interface CommonReportProps {
  onSearchSuccess?: SearchFormProps['onSuccess'];
  onSearchError?: SearchFormProps['onError'];
}
// END INTERFACES_AND_TYPES

export function Report({ onSearchSuccess, onSearchError }: CommonReportProps) {
  // START HOOKS_AND_QUERIES
  const { search } = useLocation();
  const { reportType } = useParams();
  const { logError } = useContext(AppConfig);
  const {
    featureFlags: { propertyReportFlags },
  } = useContext(AppConstants);
  const queryClient = useQueryClient();
  const queryParams = useQueryParams(search);
  const { session, setSession, useAccount } = useSession();
  const { refetch: refetchAccount } = useAccount(session.isAuthenticated);
  const isInPartialAddressesVariation = useFeatureIsOn('OAR-1579');
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const permalinkQuery = queryParams.get('permalink') || '';
  const [permalink, setPermalink] = useState(permalinkQuery);
  const [isMonitored] = useState(false);
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const getReportGetter = (type: string | undefined) => {
    if (!type || !(type in reportGetters)) {
      throw new Error(`Invalid report type: ${type}`);
    }
    return reportGetters[type as keyof typeof reportGetters];
  };

  const reportGetter = getReportGetter(reportType);
  const loading = !permalink;

  if (reportType === 'property') {
    // Append report flags before generating the new report
    queryParams.set('report_flags', JSON.stringify(propertyReportFlags));
  }

  const permalinkMutation = useMutation({
    mutationFn: () => reportGetter({ queryParams }),
    onSuccess: async (data: SearchReportResponseData) => {
      const { data: accountData } = await refetchAccount();
      setSession({
        account: accountData,
      });
      setPermalink(data.report.permalink);
    },
    onError: (error: Error, variables, context) => {
      logError('Report Route', error);

      const defaultOptions = queryClient.getDefaultOptions();

      // if mutation/query onError is overridden, we must still call the
      // global handler to make sure backend error codes are handled properly
      if (defaultOptions.mutations && defaultOptions.mutations.onError) {
        defaultOptions.mutations.onError(error, variables, context);
      }
    },
  });
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS

  useEffect(() => {
    if (
      !permalink &&
      !permalinkMutation.isError &&
      !permalinkMutation.isLoading
    ) {
      if (reportType === 'person') {
        queryParams.set(
          'showPartialAddresses',
          isInPartialAddressesVariation.toString()
        );
      }
      permalinkMutation.mutate();
    } else if (permalinkQuery && permalink !== permalinkQuery) {
      setPermalink(permalinkQuery);
    }
  }, [
    permalink,
    permalinkMutation,
    permalinkQuery,
    isInPartialAddressesVariation,
  ]);
  // END EFFECTS

  // START RENDER
  const renderLoadingState = () => {
    if (reportType === 'unclaimed-money') {
      return (
        <UnclaimedMoneyReportLoading
          variation="default"
          topImage={imgUnclaimedMoney}
        />
      );
    }
    return <ReportLoading menuItems={10} />;
  };

  return (
    <ReportContextProvider permalink={permalink} reportType={reportType || ''}>
      {loading ? (
        renderLoadingState()
      ) : (
        <Grid sx={{ bgcolor: 'background.default' }} container>
          <Sidenav onReport={true} />
          <ReportBody>
            {reportType === 'person' && (
              <PersonReport permalink={permalink} isMonitored={isMonitored} />
            )}
            {reportType === 'phone' && (
              <PhoneReport permalink={permalink} isMonitored={isMonitored} />
            )}
            {reportType === 'property' && (
              <PropertyReport permalink={permalink} isMonitored={isMonitored} />
            )}
            {reportType === 'email' && (
              <EmailReport permalink={permalink} isMonitored={isMonitored} />
            )}
            {reportType === 'username' && (
              <SocialReport permalink={permalink} />
            )}
            {reportType === 'email-fraud' && (
              <EmailFraudReport permalink={permalink} />
            )}
            {reportType === 'phone-fraud' && (
              <PhoneFraudReport permalink={permalink} />
            )}
            {reportType === 'unclaimed-money' && (
              <UnclaimedMoneyReport
                permalink={permalink}
                isMonitored={isMonitored}
              />
            )}
          </ReportBody>
        </Grid>
      )}

      <FooterSearchContainer>
        <SearchFooter
          onSearchSuccess={onSearchSuccess}
          onSearchError={onSearchError}
        />
      </FooterSearchContainer>
    </ReportContextProvider>
  );
  // END RENDER
}
